import React, {
    useEffect,
    useState,
} from 'react'

import PulseLoader from 'react-spinners/PulseLoader'

import {
    useCreateNewEvalCategoryMutation,
    useUpdateEvalCategoryMutation,
    useUpdateEvalCategoryStatusMutation,
} from '@/features/coachUserSessionEvals/evalCategorysApiSlice'


/*
|--------------------------------------------------------------------------
| Evaluation Category Form
|--------------------------------------------------------------------------
|
| First Created - 29 Oct 2026
| Author - Atul Pandey
|
| Step 12A - Centralized Evaluation Framework
|
| Supports:
|   Create Category
|   Update Category
|   Activate / Deactivate Category
|
|--------------------------------------------------------------------------
*/

const EtEvalCategoryForm = ({
    category = null,
    onDone,
    onCancel,
}) => {

    const isEditing = Boolean(category?._id)

    /*
    |--------------------------------------------------------------------------
    | 01 - Form State
    |--------------------------------------------------------------------------
    */

    const [categoryName, setCategoryName] =
        useState(category?.categoryName || '')

    const [categoryDesc, setCategoryDesc] =
        useState(category?.categoryDesc || '')

    const [active, setActive] =
        useState(category?.active !== false)

    const [errorMessage, setErrorMessage] =
        useState('')


    /*
    |--------------------------------------------------------------------------
    | 02 - Synchronize Existing Category
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        setCategoryName(
            category?.categoryName || ''
        )

        setCategoryDesc(
            category?.categoryDesc || ''
        )

        setActive(
            category?.active !== false
        )

        setErrorMessage('')

    }, [category])


    /*
    |--------------------------------------------------------------------------
    | 03 - Mutations
    |--------------------------------------------------------------------------
    */

    const [
        createCategory,
        {
            isLoading: isCreating,
        },
    ] = useCreateNewEvalCategoryMutation()

    const [
        updateCategory,
        {
            isLoading: isUpdating,
        },
    ] = useUpdateEvalCategoryMutation()

    const [
        updateCategoryStatus,
        {
            isLoading: isUpdatingStatus,
        },
    ] = useUpdateEvalCategoryStatusMutation()


    const isBusy =
        isCreating ||
        isUpdating ||
        isUpdatingStatus


    /*
    |--------------------------------------------------------------------------
    | 04 - Submit
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async event => {

        event.preventDefault()

        setErrorMessage('')

        const name = categoryName.trim()
        const description = categoryDesc.trim()

        if (!name) {

            setErrorMessage(
                'Evaluation category name is required.'
            )

            return
        }

        try {

            if (isEditing) {

                /*
                |--------------------------------------------------------------------------
                | Update Category Definition
                |--------------------------------------------------------------------------
                */

                await updateCategory({

                    id: category._id,

                    categoryName: name,

                    categoryDesc: description,

                }).unwrap()


                /*
                |--------------------------------------------------------------------------
                | Update Active Status Separately
                |--------------------------------------------------------------------------
                |
                | Existing Trinity API exposes a dedicated
                | status endpoint.
                |
                |--------------------------------------------------------------------------
                */

                if (
                    active !==
                    (category.active !== false)
                ) {

                    await updateCategoryStatus({

                        id: category._id,

                        active,

                    }).unwrap()

                }

            } else {

                /*
                |--------------------------------------------------------------------------
                | Create Category
                |--------------------------------------------------------------------------
                */

                await createCategory({

                    categoryName: name,

                    categoryDesc: description,

                    active,

                }).unwrap()

            }

            onDone?.()

        } catch (error) {

            setErrorMessage(
                error?.data?.message ||
                error?.error ||
                'Unable to save evaluation category.'
            )

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 05 - Render
    |--------------------------------------------------------------------------
    */

    return (

        <div className="rounded-lg border border-orange-200 bg-gray-50 p-3 font-poppins">

            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">

                <div>

                    <p className="text-sm font-bold text-orange-600">
                        {isEditing
                            ? 'Update Evaluation Category'
                            : 'Create Evaluation Category'}
                    </p>

                    <p className="mt-1 text-[10px] italic text-gray-500">
                        Categories organize the dimensions
                        used in coach evaluations.
                    </p>

                </div>

                {isBusy && (
                    <PulseLoader
                        color="#ea580c"
                        size={5}
                    />
                )}

            </div>


            <form
                onSubmit={handleSubmit}
                className="space-y-3"
            >

                {/* Category Name */}

                <div className="rounded bg-white p-3">

                    <label
                        htmlFor="eval-category-name"
                        className="mb-2 block text-xs font-bold text-gray-700"
                    >
                        Category Name
                    </label>

                    <input
                        id="eval-category-name"
                        type="text"
                        required
                        value={categoryName}
                        onChange={event =>
                            setCategoryName(
                                event.target.value
                            )
                        }
                        disabled={isBusy}
                        maxLength={150}
                        placeholder="e.g. Communication"
                        className="w-full rounded border border-gray-200 bg-gray-50 p-2 text-xs outline-none focus:border-orange-400"
                    />

                </div>


                {/* Description */}

                <div className="rounded bg-white p-3">

                    <label
                        htmlFor="eval-category-desc"
                        className="mb-2 block text-xs font-bold text-gray-700"
                    >
                        Description
                    </label>

                    <textarea
                        id="eval-category-desc"
                        rows={3}
                        value={categoryDesc}
                        onChange={event =>
                            setCategoryDesc(
                                event.target.value
                            )
                        }
                        disabled={isBusy}
                        placeholder="Describe the developmental area..."
                        className="w-full rounded border border-gray-200 bg-gray-50 p-2 text-xs outline-none focus:border-orange-400"
                    />

                </div>


                {/* Active Status */}

                <div className="rounded bg-white p-3">

                    <label className="flex cursor-pointer items-center gap-3">

                        <input
                            type="checkbox"
                            checked={active}
                            onChange={event =>
                                setActive(
                                    event.target.checked
                                )
                            }
                            disabled={isBusy}
                            className="accent-orange-600"
                        />

                        <div>

                            <p className="text-xs font-semibold text-gray-700">
                                Active Category
                            </p>

                            <p className="text-[10px] text-gray-500">
                                Inactive categories remain
                                available for historical reporting.
                            </p>

                        </div>

                    </label>

                </div>


                {errorMessage && (

                    <p
                        role="alert"
                        className="rounded bg-rose-50 p-2 text-[11px] text-rose-700"
                    >
                        {errorMessage}
                    </p>

                )}


                {/* Actions */}

                <div className="flex flex-wrap gap-2 rounded bg-white p-3">

                    <button
                        type="submit"
                        disabled={isBusy || !categoryName.trim()}
                        className="rounded-full bg-lime-600 px-5 py-2 text-[11px] font-bold text-white hover:bg-lime-700 disabled:opacity-50"
                    >
                        {isBusy
                            ? 'Saving...'
                            : isEditing
                                ? 'Update Category'
                                : 'Create Category'}
                    </button>

                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isBusy}
                        className="rounded-full border border-orange-200 bg-white px-5 py-2 text-[11px] font-semibold text-orange-600 hover:bg-orange-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>

                </div>

            </form>

        </div>

    )

}


export default EtEvalCategoryForm